<template>
  <div class="mtree-select-wrapper">
    <div
      ref="triggerRef"
      class="mtree-select"
      :class="{
        'mtree-select--open': isOpen,
        'mtree-select--disabled': disable,
        'mtree-select--focused': isFocused,
      }"
      :tabindex="disable ? -1 : 0"
      @click="toggle"
      @keydown="onTriggerKeydown"
      @focus="isFocused = true"
      @blur="onTriggerBlur"
    >
      <span v-if="displayText" class="mtree-select__value">{{ displayText }}</span>
      <span v-else class="mtree-select__placeholder">{{ placeholder }}</span>
      <svg
        class="mtree-select__chevron"
        :class="{ 'mtree-select__chevron--open': isOpen }"
        width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M4 6L8 10L12 6" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>

    <Teleport to="body">
      <Transition name="mtree-select-dropdown">
        <!-- focusin.stop: q-dialog ловит focusin на body и возвращает фокус себе,
             иначе курсор не удержится в поле поиска телепортированного блока. -->
        <div
          v-if="isOpen"
          ref="floatingRef"
          class="mtree-select__dropdown"
          :class="{ 'mtree-select__dropdown--no-search': !showSearchHeader }"
          :style="floatingStyles"
          @focusin.stop
        >
          <selector-search-header
            v-if="showSearchHeader"
            ref="searchHeader"
            v-model="searchText"
            :show-close-button="true"
            :use-own-clear-button="true"
            :show-free-input="showFreeInput"
            :free-input-tooltip="freeInputTooltip"
            @keyUp="emitKeyUp"
            @keyDown="emitKeyDown"
            @keyEnter="emitKeyEnter"
            @inputPressed="onFreeInputPressed"
            @close="close"
          />
          <div class="mtree-select__content" ref="contentRef">
            <slot />
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from "vue-facing-decorator";
import { computePosition, offset, flip, shift, size, autoUpdate } from "@floating-ui/dom";
import { nextTick } from "vue";
import SelectorSearchHeader from "@/components/common/aux-selector/SelectorSearchHeader.vue";

@Component({
  name: "MostaTreeSelect",
  components: { SelectorSearchHeader },
  expose: ["open", "close"],
})
export default class MostaTreeSelect extends Vue {
  @Prop({ default: "" }) displayText!: string;
  @Prop({ default: "" }) placeholder!: string;
  @Prop({ default: false }) disable!: boolean;
  @Prop({ default: 300 }) contentMaxHeight!: number;
  @Prop({ default: false }) showFreeInput!: boolean;
  @Prop({ default: "" }) freeInputTooltip!: string;
  @Prop({ default: true }) showSearchHeader!: boolean;
  @Prop({ default: 0 }) dropdownHeight!: number;

  @Emit("update:search") emitSearch(val: string) { return val; }
  @Emit("open") emitOpen() { return; }
  @Emit("close") emitClose() { return; }
  @Emit("keyUp") emitKeyUp() { return; }
  @Emit("keyDown") emitKeyDown() { return; }
  @Emit("keyEnter") emitKeyEnter() { return; }
  @Emit("freeInputPressed") emitFreeInputPressed(val?: string) { return val; }

  onFreeInputPressed() {
    this.emitFreeInputPressed(this.searchText);
  }

  isOpen = false;
  isFocused = false;
  private innerSearch = "";
  private cleanup: (() => void) | null = null;
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;
  private escapeHandler: ((e: KeyboardEvent) => void) | null = null;
  private wheelHandler: ((e: WheelEvent) => void) | null = null;

  floatingStyles: Record<string, string> = { position: "absolute", left: "0px", top: "0px" };

  get searchText(): string {
    return this.innerSearch;
  }

  set searchText(val: string) {
    this.innerSearch = val || "";
    this.emitSearch(this.innerSearch);
  }

  toggle() {
    if (this.disable) return;
    this.isOpen ? this.close() : this.open();
  }

  open() {
    if (this.disable || this.isOpen) return;
    this.isOpen = true;

    // floating-ui считает позицию асинхронно, поэтому координаты по триггеру ставим сразу:
    // телепортированный в body блок обязан быть вне потока с первого кадра.
    const rect = (this.$refs.triggerRef as HTMLElement | undefined)?.getBoundingClientRect();
    if (rect) {
      this.floatingStyles = {
        position: "absolute",
        left: `${rect.left + window.scrollX}px`,
        top: `${rect.bottom + 4 + window.scrollY}px`,
      };
    }

    this.emitOpen();
    nextTick(() => {
      this.setupFloating();
      if (this.showSearchHeader) {
        (this.$refs.searchHeader as any)?.focusField?.();
      }
    });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.isFocused = false;
    this.searchText = "";
    this.destroyFloating();
    this.emitClose();
  }

  // Пока блок открыт, уход фокуса с триггера его не закрывает: фокус штатно уезжает
  // в поле поиска внутри телепортированного блока. Закрытие идёт по клику мимо, Esc и Tab.
  onTriggerBlur() {
    if (this.isOpen) return;
    this.isFocused = false;
  }

  onTriggerKeydown(e: KeyboardEvent) {
    if (this.disable) return;

    switch (e.key) {
      case "Enter":
      case " ":
      case "ArrowDown":
        e.preventDefault();
        if (!this.isOpen) this.open();
        break;
      case "Escape":
        if (this.isOpen) {
          e.preventDefault();
          e.stopPropagation();
          this.close();
        }
        break;
      case "Tab":
        this.close();
        break;
    }
  }

  private setupFloating() {
    const trigger = this.$refs.triggerRef as HTMLElement;
    const floating = this.$refs.floatingRef as HTMLElement;
    if (!trigger || !floating) return;

    const maxHeight = this.contentMaxHeight;
    const dropdownHeight = this.dropdownHeight;
    this.cleanup = autoUpdate(trigger, floating, () => {
      computePosition(trigger, floating, {
        placement: "bottom-start",
        middleware: [
          offset(4),
          flip(),
          shift(),
          size({
            apply({ availableHeight, rects, elements }) {
              const next: Record<string, string> = {
                width: `${rects.reference.width}px`,
                maxHeight: `${Math.min(availableHeight, maxHeight)}px`,
              };
              if (dropdownHeight > 0) {
                next.height = `${Math.min(dropdownHeight, availableHeight, maxHeight)}px`;
              }
              Object.assign(elements.floating.style, next);
            },
          }),
        ],
      }).then(({ x, y }) => {
        this.floatingStyles = {
          position: "absolute",
          left: `${x}px`,
          top: `${y}px`,
        };
      });
    });

    this.outsideClickHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!trigger.contains(target) && !floating.contains(target)) {
        this.close();
      }
    };
    document.addEventListener("mousedown", this.outsideClickHandler);

    // Фокус уходит в поиск внутри телепорта, поэтому Esc на триггере не срабатывает.
    this.escapeHandler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      this.close();
    };
    document.addEventListener("keydown", this.escapeHandler, true);

    // Список на body: колесо иначе уходит на страницу справа (таблица/графики).
    this.wheelHandler = (e: WheelEvent) => this.containDropdownWheel(e);
    floating.addEventListener("wheel", this.wheelHandler, { passive: false, capture: true });
  }

  private containDropdownWheel(e: WheelEvent) {
    e.preventDefault();
    e.stopPropagation();
    const el = this.$refs.contentRef as HTMLElement | undefined;
    if (el) el.scrollTop += e.deltaY;
  }

  private destroyFloating() {
    const floating = this.$refs.floatingRef as HTMLElement | undefined;
    if (this.wheelHandler && floating) {
      floating.removeEventListener("wheel", this.wheelHandler, true);
    }
    this.wheelHandler = null;
    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }
    if (this.outsideClickHandler) {
      document.removeEventListener("mousedown", this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    if (this.escapeHandler) {
      document.removeEventListener("keydown", this.escapeHandler, true);
      this.escapeHandler = null;
    }
  }

  beforeUnmount() {
    this.destroyFloating();
  }
}
</script>

<style lang="scss" scoped>
.mtree-select-wrapper {
  margin: 4px 0;
}

.mtree-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--color-border, #D2D2D2);
  background: var(--Background-White, #FFF);
  cursor: pointer;
  outline: none;
  box-sizing: border-box;
  gap: 8px;
  user-select: none;
}

.mtree-select--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.mtree-select--focused,
.mtree-select--open {
  border-color: var(--color-accent, #3F884F);
}

.mtree-select:not(.mtree-select--disabled):hover {
  border-color: var(--color-accent, #3F884F);
}

.mtree-select__value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--Text-color-text_main, #4D4D4D);
  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
}

.mtree-select__placeholder {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #999;
  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
}

.mtree-select__chevron {
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.mtree-select__chevron--open {
  transform: rotate(180deg);
}
</style>

<style lang="scss">
.mtree-select__dropdown {
  z-index: 6000;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid var(--color-border, #D2D2D2);
  background: var(--Background-White, #FFF);
  box-shadow: none;
  box-sizing: border-box;
  overflow: hidden;
}

.mtree-select__content {
  flex: 1;
  overflow: auto;
  overscroll-behavior: contain;
  padding: 0 4px 4px;
}

.mtree-select__dropdown--no-search .mtree-select__content {
  padding: 0;
  min-height: 0;
}

.mtree-select-dropdown-enter-active,
.mtree-select-dropdown-leave-active {
  transition: opacity 0.15s ease;
}

.mtree-select-dropdown-enter-from,
.mtree-select-dropdown-leave-to {
  opacity: 0;
}
</style>
